import React, { useEffect, useState } from 'react';
import '../../components/AiResultModal.css';
import { fetchJobPoolItemAttachmentUrl } from '../../services/matchingApi.js';

const STRUCTURED_FIELDS = [
    ['job_title', '求人タイトル'],
    ['company_name', '企業名'],
    ['industry', '業界'],
    ['occupation', '職種'],
    ['salary_min', '年収下限（万円）'],
    ['salary_max', '年収上限（万円）'],
    ['work_location', '勤務地'],
    ['employment_type', '雇用形態'],
    ['company_size', '企業規模'],
    ['required_education', '必要学歴'],
    ['annual_holidays', '年間休日'],
    ['holiday_days', '休日'],
    ['overtime_info', '残業情報'],
    ['skills', 'スキル'],
    ['features_description', '特徴'],
    ['job_description_summary', '仕事内容（要約）'],
    ['job_description', '仕事内容（詳細）'],
    ['version', 'バージョン'],
];

function formatValue(value) {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
    return String(value);
}

const EMPTY_PDF_STATE = { loading: false, url: null, fileName: null, fileType: undefined, error: null };

/**
 * 添付が PDF かどうか (SCR-M-001 §4.5、Issue #1405)。
 * 応答の file_type (MIME type) で決める。
 * - undefined (応答に項目が無い＝BE の反映前) のときだけ、ファイル名の拡張子でつなぐ
 * - null・空 (BE が種類を知らない) は PDF でない側に倒す。BE はこのとき署名付き URL を attachment で
 *   発行するため、枠の中に表示しようとするとダウンロードが起きる (Issue #1405 判断 1)
 * ファイル名は利用者が付けた元の名前のため、file_type があればそちらを正とする。
 */
export function isPdfAttachment(fileType, fileName) {
    if (fileType === undefined) return /\.pdf$/i.test(fileName || '');
    if (!fileType) return false;
    return fileType.split(';')[0].trim().toLowerCase() === 'application/pdf';
}

/**
 * 求人詳細モーダル (SCR-M-001 内、T-002-U2)
 *
 * 2 タブ構成: 求人票 PDF / 構造化データ (18 項目)。
 * PDF タブ: GET /api/matching/job-pool/{id}/attachment/download_url (Issue #443、BTN-M-006) で
 * 署名付きURLを取得し、PDF のときだけ iframe プレビューする。PDF でない添付 (CSV 等) は iframe に渡すと
 * ブラウザが表示できずダウンロードが起きるため、文言とダウンロードのリンクを出す (Issue #1405)。
 */
function JobDetailModal({ job, onClose }) {
    const [activeTab, setActiveTab] = useState('structured');
    const [pdfState, setPdfState] = useState(EMPTY_PDF_STATE);

    useEffect(() => {
        // モーダルを開くたびに(job 変更時)PDF 読込状態をリセットする
        setPdfState(EMPTY_PDF_STATE);
        setActiveTab('structured');
    }, [job]);

    useEffect(() => {
        if (activeTab !== 'pdf' || !job || pdfState.url || pdfState.loading || pdfState.error) return;

        let cancelled = false;
        setPdfState((prev) => ({ ...prev, loading: true }));
        (async () => {
            const result = await fetchJobPoolItemAttachmentUrl(job.id);
            if (cancelled) return;
            if (result.ok) {
                setPdfState({
                    loading: false,
                    url: result.downloadUrl,
                    fileName: result.fileName,
                    fileType: result.fileType,
                    error: null,
                });
            } else {
                setPdfState({ ...EMPTY_PDF_STATE, error: result.errorMessage });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, job]);

    if (!job) return null;

    return (
        <div className="ai-modal-overlay" onClick={onClose}>
            <div className="ai-modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="ai-modal-header">
                    <h3>{job.company_name} / {job.job_title}</h3>
                    <button className="ai-modal-close" onClick={onClose} aria-label="閉じる">×</button>
                </div>

                <div className="detail-tabs" style={{ padding: '0 20px' }}>
                    <button
                        type="button"
                        className={`tab-button ${activeTab === 'pdf' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pdf')}
                    >
                        求人票 PDF
                    </button>
                    <button
                        type="button"
                        className={`tab-button ${activeTab === 'structured' ? 'active' : ''}`}
                        onClick={() => setActiveTab('structured')}
                    >
                        構造化データ
                    </button>
                </div>

                <div className="ai-modal-body">
                    {activeTab === 'pdf' ? (
                        pdfState.loading ? (
                            <div className="loading-message">求人票を読み込み中...</div>
                        ) : pdfState.error ? (
                            <p className="matching-filter-empty">{pdfState.error}</p>
                        ) : pdfState.url && isPdfAttachment(pdfState.fileType, pdfState.fileName) ? (
                            <iframe
                                src={pdfState.url}
                                title={pdfState.fileName || '求人票'}
                                className="matching-pdf-frame"
                            />
                        ) : pdfState.url ? (
                            <div className="job-attachment-non-pdf">
                                <p className="matching-filter-empty">
                                    この求人票は PDF ではないため、ここでは表示できません。
                                </p>
                                {/* 新しいタブで開き、画面を離れない。PDF でない添付の URL は BE が attachment で発行する */}
                                <a
                                    href={pdfState.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="job-attachment-download-link"
                                >
                                    求人票ファイルをダウンロード（{pdfState.fileName || '求人票'}）
                                </a>
                            </div>
                        ) : null
                    ) : (
                        <table className="matching-structured-table">
                            <tbody>
                                {STRUCTURED_FIELDS.map(([key, label]) => (
                                    <tr key={key}>
                                        <th>{label}</th>
                                        <td>{formatValue(job[key])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="ai-modal-footer">
                    <button className="modal-button-confirm" onClick={onClose}>閉じる</button>
                </div>
            </div>
        </div>
    );
}

export default JobDetailModal;
